import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clientApi } from '../../lib/http';
import logo from '../../assets/logo.png';

const ConfirmEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const hasConfirmed = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid confirmation link. The link may be expired or already used.');
      return;
    }

    // Prevent double execution in React StrictMode
    if (hasConfirmed.current) {
      return;
    }
    hasConfirmed.current = true;

    const confirmEmail = async () => {
      try {
        await clientApi.post('/auth/confirm', { confirmation_token: token });
        setStatus('success');
      } catch (err: any) {
        setStatus('error');
        const message = err.response?.data?.error || err.response?.data?.errors?.[0] || 'Failed to confirm email. The link may be expired or already used.';
        setErrorMessage(message);
      }
    };

    confirmEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex flex-col p-4">
      <div className="w-full max-w-md mx-auto flex flex-col flex-1">
        {/* Logo */}
        <div className="flex justify-center pt-4">
          <img src={logo} alt="BookDay" className="h-12 w-auto" />
        </div>

        <div className="flex-1" />

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {status === 'loading' && (
            <>
              {/* Loading Spinner */}
              <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-purple-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Confirming your email...
              </h2>

              <p className="text-gray-600">
                Please wait while we verify your email address.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              {/* Success Icon */}
              <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Email confirmed!
              </h2>

              <p className="text-gray-600 mb-6">
                Your email has been verified successfully. You can now sign in to your account.
              </p>

              <div className="bg-green-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-green-800">
                  Welcome to BookDay! You're all set to start booking appointments or managing your schedule.
                </p>
              </div>

              <Link
                to="/auth/select"
                className="inline-block w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                Sign in to your account
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              {/* Error Icon */}
              <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Confirmation failed
              </h2>

              <p className="text-gray-600 mb-6">
                {errorMessage}
              </p>

              <div className="space-y-3">
                <Link
                  to="/auth/select"
                  className="inline-block w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
                >
                  Go to sign in
                </Link>

                <p className="text-sm text-gray-500">
                  Need help?{' '}
                  <a href="mailto:info@bookday.me" className="text-purple-600 hover:underline">
                    Contact support
                  </a>
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex-1" />

        {/* Footer */}
        <div className="text-center pb-4 text-sm text-gray-500">
          <p>
            <a href="/terms" className="text-purple-600 hover:underline">
              Terms of Service
            </a>
            {' · '}
            <a href="/privacy" className="text-purple-600 hover:underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmEmail;
